/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import React, { useEffect } from "react";
import { reportFrontendError } from "@/lib/sentryError";
import { onCLS, onFID, onLCP, onTTFB } from "web-vitals";
import Footer from "@/shared/Footer";
import { Toaster } from "react-hot-toast";

// ✅ Send performance metrics to backend
function sendToServer(metric: any) {
  fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/metrics`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: metric.name,
      value: metric.value,
      route: window.location.pathname,
      userAgent: navigator.userAgent,
    }),
  }).catch((err) => console.warn("Metric send failed:", err));
}

function initWebVitals() {
  onCLS(sendToServer);
  onFID(sendToServer);
  onLCP(sendToServer);
  onTTFB(sendToServer);
}

//  Error Boundary for React render errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportFrontendError({
      message: error.message,
      stack: error.stack,
      extra: info,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center py-20 text-red-600 text-xl">
          Something went wrong.
        </div>
      );
    }
    return this.props.children;
  }
}

// ✅ Main Layout with global listeners and web vitals
export default function MainSiteLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    // Web Vitals (performance metrics)
    initWebVitals();

    // Runtime and async error tracking
    const runtimeErrorHandler = (event: ErrorEvent) => {
      reportFrontendError(event.error || { message: event.message });
    };
    const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      reportFrontendError(event.reason);
    };

    window.addEventListener("error", runtimeErrorHandler);
    window.addEventListener("unhandledrejection", unhandledRejectionHandler);

    return () => {
      window.removeEventListener("error", runtimeErrorHandler);
      window.removeEventListener("unhandledrejection", unhandledRejectionHandler);
    };
  }, []);

  return (
    <ErrorBoundary>
      <main>{children}
          <Toaster position="top-center" />
      </main>
    </ErrorBoundary>
  );
}
